import type { LegacyMemberResult } from '~/services/LegacyEnterpriseServer/types'

export interface EnterpriseJoinRecord {
  requestId: string
  status: 'UNKNOWN' | 'RESERVED' | 'APPLIED'
}

type JoinResponse = { code: string; message?: string; data?: LegacyMemberResult | null }
type JoinOutcome = { state: 'success' | 'pending' | 'failed' | 'cancelled'; message?: string }
// 408 / 422 / 429 / 500 均不能证明服务器没有受理，须保留原操作编号。
const DEFINITIVE_CONFIRM_REJECTIONS = new Set(['400', '403', '407', '411', '413', '425', '426', '427', '428', '430', '431', '432'])

export function enterpriseJoinStorageKey(userId: unknown, teamId: unknown, site: string): string {
  return Number(userId) > 0 && Number(teamId) > 0 && ['AI', 'VIP'].includes(site)
    ? `enterprise-member-join:${site}:${userId}:${teamId}` : ''
}

export function readEnterpriseJoinRecord(key: string): EnterpriseJoinRecord | null {
  try {
    if (!key || typeof window === 'undefined') return null
    const record = JSON.parse(window.sessionStorage.getItem(key) || 'null')
    return record && /^[a-zA-Z0-9_-]{8,64}$/.test(record.requestId || '')
      && ['UNKNOWN', 'RESERVED', 'APPLIED'].includes(record.status) ? record : null
  } catch { return null }
}

export function saveEnterpriseJoinRecord(key: string, record: EnterpriseJoinRecord | null) {
  try {
    if (!key || typeof window === 'undefined') return
    if (record) window.sessionStorage.setItem(key, JSON.stringify(record))
    else window.sessionStorage.removeItem(key)
  } catch { /* 浏览器禁用存储时，当前页面仍保留操作编号。 */ }
}

/** 同意只提交一次；后续只查询原操作，应用完成后独立等待用户资料同步。 */
export async function runEnterpriseJoin(options: {
  record: EnterpriseJoinRecord | null
  createRequestId: () => string
  confirm: (requestId: string) => Promise<JoinResponse>
  query: (requestId: string) => Promise<JoinResponse>
  synchronize: (canApply: () => boolean) => Promise<boolean>
  remember: (record: EnterpriseJoinRecord | null) => void
  isCurrent: () => boolean
  budgetMs?: number
  intervalMs?: number
}): Promise<JoinOutcome> {
  const deadline = Date.now() + (options.budgetMs ?? 30_000)
  const current = () => options.isCurrent() && Date.now() < deadline
  let record = options.record
  const timeout = Symbol('timeout')
  async function withinBudget<T>(action: () => Promise<T>): Promise<T | typeof timeout> {
    const remaining = deadline - Date.now()
    if (remaining <= 0 || !options.isCurrent()) return timeout
    let timer: ReturnType<typeof setTimeout> | undefined
    try {
      return await Promise.race([
        action(),
        new Promise<typeof timeout>(resolve => { timer = setTimeout(() => resolve(timeout), remaining) }),
      ])
    } finally { if (timer) clearTimeout(timer) }
  }
  try {
    let submit = !record
    let mayReplayUncertainSubmit = Boolean(record?.status === 'UNKNOWN')
    if (!record) {
      record = { requestId: options.createRequestId(), status: 'UNKNOWN' }
      if (!current()) return { state: 'cancelled' }
      options.remember(record)
    }
    while (current()) {
      if (record.status === 'APPLIED') {
        const synced = await withinBudget(() => options.synchronize(current))
        if (!options.isCurrent()) return { state: 'cancelled' }
        if (synced === true && current()) {
          options.remember(null)
          return { state: 'success' }
        }
        if (synced === timeout) break
      } else {
        const wasSubmit = submit
        const response = await withinBudget(() => submit
          ? options.confirm(record!.requestId) : options.query(record!.requestId))
        submit = false
        if (!options.isCurrent()) return { state: 'cancelled' }
        if (response === timeout || !current()) break
        if (response.code !== '1') {
          // 首次请求明确被拒绝，没有已受理操作；下一次允许重新发起。
          if (wasSubmit && !options.record && DEFINITIVE_CONFIRM_REJECTIONS.has(response.code)) {
            options.remember(null)
            return { state: 'failed', message: response.message }
          }
          // 首次网络结果不确定且查不到操作时，只能重放相同编号恢复，不能新建操作。
          if (!wasSubmit && response.code === '413' && mayReplayUncertainSubmit) {
            mayReplayUncertainSubmit = false
            submit = true
            continue
          }
          return { state: 'pending', message: response.message }
        }
        mayReplayUncertainSubmit = false
        if (response.data?.status === 'REJECTED') {
          options.remember(null)
          return { state: 'failed' }
        }
        record = {
          requestId: response.data?.requestId || record.requestId,
          status: response.data?.status === 'APPLIED' || (wasSubmit && !response.data?.status)
            ? 'APPLIED' : response.data?.status === 'RESERVED' ? 'RESERVED' : 'UNKNOWN',
        }
        options.remember(record)
        if (record.status === 'APPLIED') continue
      }
      const waited = await withinBudget(() => new Promise<void>(resolve => setTimeout(resolve, options.intervalMs ?? 1_000)))
      if (waited === timeout) break
    }
    return { state: options.isCurrent() ? 'pending' : 'cancelled' }
  } catch (error: any) {
    return options.isCurrent() ? { state: 'pending', message: error?.message } : { state: 'cancelled' }
  }
}
